import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { templateLabel } from '@/features/campaigns/types';
import { useTtsFlowList } from '@/features/tts-flow/api';
import { TtsFlowPreviewModal } from '@/features/tts-flow/components/TtsFlowPreviewModal';
import { useVoiceWebhookList } from '@/features/webhooks/api';
import { MODULE_IDS } from '@/lib/constants/modules';
import { Eye, Repeat } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useUserModules } from '../../api';
import { formatScheduleTime, resolveTemplateId } from '../../payload';
import {
  RETRY_INTERVAL_MAX,
  RETRY_INTERVAL_MIN,
  templateFeatures,
  type VoiceCampaignFormValues,
} from '../../types';
import { ContinuousScheduleCard } from '../ContinuousScheduleCard';

interface Props {
  values: VoiceCampaignFormValues;
  setValues: (patch: Partial<VoiceCampaignFormValues>) => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-t border-border py-2 text-sm first:border-t-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">
        {value || <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

export function StepReview({ values, setValues }: Props) {
  const finalTemplateId = resolveTemplateId(values);
  const features = templateFeatures(values.templateId, values.templateIdByTTS);
  const sched = values.scheduleDate ? new Date(values.scheduleDate) : undefined;
  /** Retries are template-bound — TTS Simple and Continuous skip them. */
  const modules = useUserModules();
  const ttsFlowId = values.ttsId || values?.selectedTTSFlowId || '';
  const ttsFlows = useTtsFlowList();
  const pickedFlow = useMemo(
    () => (ttsFlows.data ?? []).find((f) => String(f.id) === ttsFlowId),
    [ttsFlows.data, ttsFlowId],
  );

  const moduleNames = useMemo(
    () => new Set((modules.data ?? []).map((m) => m.moduleName)),
    [modules.data],
  );

  const isContinuous = moduleNames.has('Continuous Campaign');
  const hasRingDurationModule = moduleNames.has('Ring Duration');

  const [previewOpen, setPreviewOpen] = useState(false);

  const voiceHooks = useVoiceWebhookList();
  const webhookLabel = useMemo(() => {
    if (!values.sendWebhook) return 'Disabled';
    const match = (voiceHooks.data ?? []).find((w) => String(w.id) === values.webhookId);
    return match ? `${match.webhookName} — ${match.event}` : `#${values.webhookId}`;
  }, [values.sendWebhook, values.webhookId, voiceHooks.data]);

  const hasRetryModule = useMemo(
    () => (modules.data ?? []).some((m) => Number(m.moduleId) === MODULE_IDS.RETRY),
    [modules.data],
  );
  const showRetry =
    hasRetryModule && !features.hasTtsFlow && values.templateId !== '7' && !isContinuous;

  const scheduleSummary = values.sendLater
    ? formatScheduleTime(values.scheduleDate)
    : 'Send immediately';

  const contactSummary = (() => {
    if (values.baseFileMode === 'contactList') {
      const count = values.contactList
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean).length;
      return `Pasted ${count} number${count === 1 ? '' : 's'}`;
    }
    if (values.baseFileMode === 'newBaseFile') {
      return `Upload "${values.baseName || 'new file'}"`;
    }
    return values.oldBaseId ? `Existing list #${values.oldBaseId}` : 'Existing list (none picked)';
  })();

  return (
    <div className="space-y-4">
      {!isContinuous && (
        <Card>
          <CardHeader>
            <CardTitle>Schedule</CardTitle>
            <CardDescription>Send immediately, or pick a future date and time.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="text-sm font-medium">Schedule for later</div>
                <div className="text-xs text-muted-foreground">
                  When off, the campaign starts as soon as you submit.
                </div>
              </div>
              <Switch
                checked={values.sendLater}
                onCheckedChange={(v) => setValues({ sendLater: v })}
              />
            </div>

            {values.sendLater && (
              <div className="space-y-2">
                <Label>
                  Send at
                  <span className="ml-1 text-destructive">*</span>
                </Label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <DatePicker
                    value={sched}
                    onChange={(d) => {
                      if (!d) {
                        setValues({ scheduleDate: '' });
                        return;
                      }
                      // Keep the time already chosen; default to now + 15 min.
                      const base = sched ?? new Date(Date.now() + 15 * 60 * 1000);
                      const next = new Date(d);
                      next.setHours(base.getHours(), base.getMinutes(), 0, 0);
                      setValues({ scheduleDate: next.toISOString() });
                    }}
                    placeholder="Pick a date"
                    fromDate={new Date()}
                  />
                  <Input
                    type="time"
                    aria-label="Send time"
                    value={
                      sched
                        ? `${String(sched.getHours()).padStart(2, '0')}:${String(sched.getMinutes()).padStart(2, '0')}`
                        : ''
                    }
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(':').map(Number);
                      if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m))
                        return;
                      const next = sched ? new Date(sched) : new Date();
                      next.setHours(h, m, 0, 0);
                      setValues({ scheduleDate: next.toISOString() });
                    }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showRetry && (
        <Card>
          <CardHeader>
            <CardTitle>Retry</CardTitle>
            <CardDescription>
              Re-dial unanswered or failed numbers up to N times, spaced by the chosen interval.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="retries" className="inline-flex items-center gap-2">
                  <Repeat className="h-3.5 w-3.5 text-muted-foreground" />
                  Retries (range: 0-2)
                </Label>
                <Input
                  id="retries"
                  inputMode="numeric"
                  min={0}
                  max={2}
                  value={String(values.retries)}
                  onChange={(e) => {
                    const raw = Number(e.target.value.replace(/\D/g, '')) || 0;
                    setValues({ retries: Math.min(2, Math.max(0, raw)) });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="retry-interval">Retry interval (range: 15-60 min)</Label>
                <Input
                  id="retry-interval"
                  type="number"
                  min={RETRY_INTERVAL_MIN}
                  max={RETRY_INTERVAL_MAX}
                  step={1}
                  placeholder={`From (${RETRY_INTERVAL_MIN}-${RETRY_INTERVAL_MAX})`}
                  value={String(values.retryInterval)}
                  onChange={(e) =>
                    setValues({
                      retryInterval: Number(e.target.value.replace(/\D/g, '')) || 0,
                    })
                  }
                  onBlur={(e) => {
                    let value = Number(e.target.value);
                    if (!value || value < RETRY_INTERVAL_MIN) value = RETRY_INTERVAL_MIN;
                    if (value > RETRY_INTERVAL_MAX) value = RETRY_INTERVAL_MAX;
                    setValues({ retryInterval: value });
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {hasRingDurationModule && Number(values.ringDuration) > 0 && (
        <Card>
          <CardContent className="space-y-3 pt-5">
            <CardTitle>Ring Duration</CardTitle>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="ring-duration">Ring Duration (range: 10-30 seconds)</Label>
                <Input
                  id="ring-duration"
                  type="number"
                  min={10}
                  max={30}
                  placeholder="10-30"
                  value={String(values.ringDuration)}
                  onChange={(e) => setValues({ ringDuration: Number(e.target.value) || 10 })}
                  onBlur={(e) => {
                    let value = Number(e.target.value);
                    if (!value || value < 10) value = 10;
                    if (value > 30) value = 30;
                    setValues({ ringDuration: value });
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Review</CardTitle>
          <CardDescription>
            Double-check the details before submitting. You can step back to fix anything.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl>
            <Row label="Name" value={values.campaignName} />
            <Row
              label="Type"
              value={`${templateLabel(Number(finalTemplateId))} (#${finalTemplateId})`}
            />
            <Row label="Schedule" value={scheduleSummary} />
            {showRetry && (
              <Row
                label="Retries"
                value={`${values.retries} attempt(s), ${values.retryInterval} min apart`}
              />
            )}
            <Row label="Contacts" value={contactSummary} />
            <Row
              label="Locations"
              value={
                values.locationIds.length > 0
                  ? `${values.locationIds.length} selected`
                  : 'All locations'
              }
            />
            <Row
              label="Dedicated CLIs"
              value={
                values.selectedCLI.length > 0
                  ? `${values.selectedCLI.length} selected`
                  : 'Shared pool'
              }
            />
            {ttsFlowId && (
              <Row
                label="TTS Flow"
                value={
                  <span className="inline-flex items-center gap-2">
                    {pickedFlow?.ttsFlowName ?? `#${ttsFlowId}`}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1.5 text-xs"
                      onClick={() => setPreviewOpen(true)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </span>
                }
              />
            )}

            <Row label="Webhook" value={webhookLabel} />
            <Row
              label="SMS"
              value={
                [
                  values.sendSmsSuccess && 'on success',
                  values.sendSmsFail && 'on failure',
                  values.sendSmsDtmf && 'on DTMF',
                  values.sendSmsPatchSuccess && 'on patch success',
                  values.sendSmsPatchFail && 'on patch failure',
                ]
                  .filter(Boolean)
                  .join(', ') || 'None'
              }
            />
          </dl>
        </CardContent>
      </Card>
      {isContinuous && (
        <ContinuousScheduleCard
          startDate={values.startDate}
          endDate={values.endDate}
          scheduleJson={values.continuousSchedule}
          continueWithBase={values.continueWithBase}
        />
      )}
      {previewOpen && ttsFlowId && (
        <TtsFlowPreviewModal
          rowId={Number(ttsFlowId)}
          flowName={pickedFlow?.ttsFlowName ?? `Flow #${ttsFlowId}`}
          templateId={String(pickedFlow?.templateId ?? finalTemplateId)}
          createdAt={pickedFlow?.createdAt}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}
